import { Anchor, Grid, Group, Modal, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Condition, Encounter, Patient } from '@medplum/fhirtypes';
import { killEvent, useMedplum } from '@medplum/react';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConceptBadge } from '../ConceptBadge';
import { ExternalCode } from './ExternalCode';
import { GeneticDiseaseDialog } from './GeneticDiseaseDialog';

// -----------------------------------------------------------------------------
export interface GeneticDiseaseProps {
  readonly patient: Patient;
  readonly encounter?: Encounter;
  readonly geneticDiseases: Condition[];
  readonly onClickResource?: (resource: Condition) => void;
}

export const GeneticDisease: FC<GeneticDiseaseProps> = ({
  patient,
  encounter,
  geneticDiseases,
}) => {
  const medplum = useMedplum();
  const { t } = useTranslation(['patient-pages']);
  const [diseases, setDiseases] = useState<Condition[]>(geneticDiseases);
  const [editDisease, setEditDisease] = useState<Condition>();
  const [opened, { open, close }] = useDisclosure(false);

  // ---------------------------------------------------------------------------
  const handleSubmit = useCallback(
    async (disease: Condition) => {
      if (disease.id) {
        const updatedDisease = await medplum.updateResource(disease);
        setDiseases(
          diseases.map((p) => (p.id === updatedDisease.id ? updatedDisease : p))
        );
      } else {
        const newDisease = await medplum.createResource(disease);
        setDiseases([...diseases, newDisease]);
      }

      setEditDisease(undefined);
      close();
    },
    [medplum, diseases, close]
  );

  // ---------------------------------------------------------------------------
  return (
    <>
      <Group justify="space-between">
        <Text fz="md" fw={700}>
          {t('summaryPage.summary.geneticDiseases.title')}
        </Text>
        <Anchor
          component="button"
          onClick={(e) => {
            killEvent(e);
            setEditDisease(undefined);
            open();
          }}
        >
          + {t('summaryPage.summary.geneticDiseases.add')}
        </Anchor>
      </Group>
      {diseases.length > 0 ? (
        <Stack>
          {diseases.map((problem) => (
            <Stack key={problem.id} gap="xs">
              <Grid>
                <Grid.Col span={2}>
                  {problem.onsetDateTime?.substring(0, 4) ?? '-'}
                </Grid.Col>
                <Grid.Col span={10}>
                  <ConceptBadge<Condition>
                    key={problem.id}
                    resource={problem}
                    onClick={(d) => {
                      setEditDisease(d);
                      open();
                    }}
                  />
                  <Grid align="center">
                    <Grid.Col span={6}>
                      <ExternalCode condition={problem} system="omim" />
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <ExternalCode condition={problem} system="orpha" />
                    </Grid.Col>
                  </Grid>
                </Grid.Col>
              </Grid>
            </Stack>
          ))}
        </Stack>
      ) : (
        <Text>{t('summaryPage.summary.geneticDiseases.none')}</Text>
      )}
      <Modal
        opened={opened}
        onClose={close}
        title={
          editDisease
            ? t('summaryPage.summary.geneticDiseases.edit')
            : t('summaryPage.summary.geneticDiseases.addNew')
        }
      >
        <GeneticDiseaseDialog
          patient={patient}
          encounter={encounter}
          condition={editDisease}
          onSubmit={handleSubmit}
        />
      </Modal>
    </>
  );
};
